import React, { useState, useRef, useCallback, useEffect, useLayoutEffect, cloneElement } from 'react';
import { createPortal } from 'react-dom';
import styles from './Tooltip.module.css';
import { getClassName } from '../../utils/className.js';

const SHOW_DELAY_MS = 250;
const VIEWPORT_MARGIN = 4;
const ANCHOR_GAP = 6;

/**
 * A lightweight hover/focus tooltip with no Material UI dependency.
 *
 * Portals into the nearest ancestor `<dialog>` element if the anchor is
 * inside one, or `document.body` otherwise. This matters specifically
 * because a `<dialog>` shown via `showModal()` (as `BaseDialog` does) is
 * promoted to the browser's top layer, rendered above all regular DOM
 * content regardless of z-index — a tooltip portaled to `document.body`
 * from inside one would be invisible, hidden behind the dialog itself.
 * Positioning is `position: fixed` in viewport coordinates (via
 * `getBoundingClientRect`), so it also can't be clipped by an `overflow:
 * hidden`/`auto` ancestor the way an absolutely-positioned child could be.
 *
 * @component
 * @param {Object} props
 * @param {React.ReactNode} [props.title] - Tooltip content — a plain string, or richer content (e.g. a list of items joined with `<br/>`). Falsy renders `children` unwrapped with no tooltip behavior.
 * @param {'top'|'bottom'} [props.placement] - Preferred side to open on; falls back to the other side if there's no room. Defaults to 'top'.
 * @param {boolean} [props.light] - Inverted color scheme (white background, dark text) instead of the default dark bubble.
 * @param {boolean} [props.hoverOnly] - If true, only mouse hover shows the tooltip — focus (from a click or Tab) never triggers it. Use for a trigger button whose click opens something else (a dialog, a download), where a focus-driven tooltip would linger or flash open. Defaults to false (hover and focus both show it, for keyboard accessibility).
 * @param {React.ReactElement} props.children - A single element to anchor the tooltip to. Must be able to hold a ref (native DOM elements always can).
 */
const Tooltip = ({ title, placement = 'top', light, hoverOnly = false, children }) => {
  const anchorRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState(null);
  const bubbleRef = useRef(null);
  const showTimeoutRef = useRef(null);

  const updatePosition = useCallback(() => {
    const anchor = anchorRef.current;
    const bubble = bubbleRef.current;
    if (!anchor || !bubble) return;

    const anchorRect = anchor.getBoundingClientRect();
    const bubbleRect = bubble.getBoundingClientRect();

    let top = placement === 'bottom'
      ? anchorRect.bottom + ANCHOR_GAP
      : anchorRect.top - bubbleRect.height - ANCHOR_GAP;
    // Flip to the other side if the preferred one has no room.
    if (placement === 'bottom' && top + bubbleRect.height > window.innerHeight - VIEWPORT_MARGIN) {
      top = anchorRect.top - bubbleRect.height - ANCHOR_GAP;
    } else if (placement !== 'bottom' && top < VIEWPORT_MARGIN) {
      top = anchorRect.bottom + ANCHOR_GAP;
    }

    const maxLeft = window.innerWidth - bubbleRect.width - VIEWPORT_MARGIN;
    const left = Math.min(Math.max(anchorRect.left, VIEWPORT_MARGIN), Math.max(VIEWPORT_MARGIN, maxLeft));

    setPos({ top, left });
  }, [placement]);

  useLayoutEffect(() => {
    if (!visible) return;
    updatePosition();
  }, [visible, title, updatePosition]);

  useEffect(() => {
    if (!visible) return undefined;
    window.addEventListener('scroll', updatePosition, true);
    window.addEventListener('resize', updatePosition);
    return () => {
      window.removeEventListener('scroll', updatePosition, true);
      window.removeEventListener('resize', updatePosition);
    };
  }, [visible, updatePosition]);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor || !title) return undefined;

    const handleShow = () => {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = setTimeout(() => setVisible(true), SHOW_DELAY_MS);
    };
    const handleHide = () => {
      clearTimeout(showTimeoutRef.current);
      setVisible(false);
    };

    anchor.addEventListener('mouseenter', handleShow);
    anchor.addEventListener('mouseleave', handleHide);
    if (!hoverOnly) {
      anchor.addEventListener('focus', handleShow);
      anchor.addEventListener('blur', handleHide);
    }

    return () => {
      clearTimeout(showTimeoutRef.current);
      anchor.removeEventListener('mouseenter', handleShow);
      anchor.removeEventListener('mouseleave', handleHide);
      if (!hoverOnly) {
        anchor.removeEventListener('focus', handleShow);
        anchor.removeEventListener('blur', handleHide);
      }
    };
  }, [title, hoverOnly]);

  if (!title) return children;

  const anchor = cloneElement(children, { ref: anchorRef });

  // Read anchorRef.current directly at render time rather than caching the
  // target in state: it's null only on the very first render (before the ref
  // commits), and the bubble is invisible (off-screen) until a hover makes
  // it visible, by which point a re-render has already happened and the ref
  // is populated — so the transient first-render fallback never shows.
  const portalTarget = typeof document !== 'undefined'
    ? (anchorRef.current && anchorRef.current.closest('dialog')) || document.body
    : null;

  const bubble = portalTarget && createPortal(
    <span
      ref={bubbleRef}
      role="tooltip"
      className={getClassName(styles.tooltipBubble, {
        [styles.tooltipBubbleLight]: light,
        [styles.tooltipBubbleVisible]: visible && !!pos,
      })}
      style={{ top: pos ? pos.top : -9999, left: pos ? pos.left : -9999 }}
    >
      {title}
    </span>,
    portalTarget
  );

  return (
    <>
      {anchor}
      {bubble}
    </>
  );
};

export default Tooltip;
